import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import router from '@/router/Router.jsx';
import defaultOptions from '@/shared/config/reactQuery.js';
import ToastProvider, { useToast } from '@/shared/context/ToastContext.jsx';
import { AuthProvider } from '@/shared/context/AuthContext.jsx';
import { ModalProvider } from '@/shared/context/ModalContext.jsx';
import ModalContainer from '@/shared/components/container/ModalContainer.jsx';
import useThemeSync from '@/shared/hooks/useThemeSync.js';
import OfflineOverlay from '@/shared/components/pwa/OfflineOverlay.jsx';
import PwaPromptContainer from '@/shared/components/container/PwaPromptContainer.jsx';
import GlobalErrorBoundary from '@/shared/components/error/GlobalErrorBoundary.jsx';
import SplashScreen from '@/shared/components/loading/SplashScreen.jsx';

const queryClient = new QueryClient({
  defaultOptions,
});

window.queryClient = queryClient;

function ToastInitializer() {
  const { showToast } = useToast();
  window.showToast = showToast;
  return null;
}

function App() {
  useThemeSync();
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <ModalProvider>
              <ToastInitializer />
              <AnimatePresence mode="wait">
                {showSplash && <SplashScreen />}
              </AnimatePresence>
              <RouterProvider router={router} />
              <ModalContainer />
              <PwaPromptContainer />
              <OfflineOverlay />
            </ModalProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
